import { useMemo } from "react";

import type { ChartTableSelection } from "../../lib/chartTables.js";
import { SHARED_FULL_WEEKS_CRITERIA, SHARED_TABLES_CRITERIA } from "../../lib/chartTables.js";
import {
  dailyChartBarAxisProps,
  dailyChartRotateAxisLabels,
  formatDashboardDayLabelLong,
} from "../../lib/dashboardDayLabel.js";
import {
  formatWeekAxisLabel,
  FULL_WEEKS_EMPTY_MESSAGE,
  groupFullMondayWeeks,
  shiftReportingDayKey,
  type UptimeWeekBucket,
} from "../../lib/uptimeWeekBuckets.js";
import { useSharedChartTables } from "../../lib/useSharedChartTables.js";
import type {
  FilterState,
  HealthData,
  HealthDeviceDailyUptime,
  OpenRawEventsHandler,
} from "../../types.js";
import { ChartCard } from "../ChartCard.js";
import type { BarChartItem } from "../charts/BarChart.js";
import { BarChart } from "../charts/BarChart.js";
import { ChartTablePicker } from "./ChartTablePicker.js";

const WARN_FILL = "var(--color-warn)";
const ERR_FILL = "var(--color-err)";

/** Above this, count labels on the bar tops overlap and are omitted. */
const VALUE_LABEL_MAX_BARS = 7;

type IssueDay = {
  day: string;
  warningCount: number;
  errorCount: number;
  capacityHours: number;
};

function weekTotals(
  week: UptimeWeekBucket,
  byDay: ReadonlyMap<string, IssueDay>,
): { warningCount: number; errorCount: number } {
  let warningCount = 0;
  let errorCount = 0;
  for (let i = 0; i < 7; i++) {
    const day = shiftReportingDayKey(week.monday, i);
    const row = day ? byDay.get(day) : undefined;
    warningCount += row?.warningCount ?? 0;
    errorCount += row?.errorCount ?? 0;
  }
  return { warningCount, errorCount };
}

/** Fleet-additive capacity hours for selected tables (Full weeks completeness). */
function capacityByDayFromDevices(
  byDevice: HealthDeviceDailyUptime[],
  selectedIds: ReadonlySet<string>,
): Map<string, number> {
  const byDay = new Map<string, number>();
  for (const device of byDevice) {
    if (!selectedIds.has(device.deviceId)) continue;
    for (const row of device.dailyUptime) {
      byDay.set(row.day, (byDay.get(row.day) ?? 0) + Math.max(0, row.capacityHours));
    }
  }
  return byDay;
}

const CRITERIA = `Source: device_events where event_name in ('client_error','console_warning','console_error') — same set as Top Issues / Issues by Hour.

Severity (stack segments):
• **warning** (yellow) if client_error with properties.log_level warning, or console_warning
• **error** (red) otherwise

Y-axis: count(*) per reporting day (dense days in the filter window). Bars are stacked warnings + errors.

**Full weeks** (checkbox, off by default; shared URL \`weeks=1\`): one bar per complete **Monday–Sunday** whose seven days are all inside the filter window and each have a full day's capacity (today-so-far and future days do not count — same rule as Health Uptime by day). Counts are **summed**. Partial weeks at the edges are omitted.

${SHARED_TABLES_CRITERIA}

${SHARED_FULL_WEEKS_CRITERIA}

Value labels above bars are omitted when there are more than 7 days or weeks. Axis labels: **D/M** + weekday initial; weekends bold white. Dense ranges rotate −90°.

Segment click: Raw Events → client_error with that severity bucket and the day (or week) as the date range. console_* rows are included in the chart but not in that event_name filter (same limitation as the Top Issues ⚠/⛔ buttons).`;

export function IssuesByDayCard({
  rows: fleetRows,
  devices,
  dailyUptimeByDevice,
  issuesByDeviceDay,
  tableCount: fleetTableCount,
  chartTables,
  onChartTablesChange,
  fullWeeks,
  onFullWeeksChange,
  filters,
  onOpenRawEvents,
}: {
  rows: HealthData["issuesByDay"];
  devices: HealthData["devices"];
  dailyUptimeByDevice: HealthDeviceDailyUptime[];
  issuesByDeviceDay: HealthData["issuesByDeviceDay"];
  tableCount: number;
  chartTables: ChartTableSelection;
  onChartTablesChange: (next: ChartTableSelection) => void;
  fullWeeks: boolean;
  onFullWeeksChange: (next: boolean) => void;
  filters: FilterState;
  onOpenRawEvents: OpenRawEventsHandler;
}) {
  const tables = useSharedChartTables(devices, chartTables, onChartTablesChange, filters);

  const rows = useMemo((): IssueDay[] => {
    if (tables.useFleetSeries) {
      return fleetRows.map((row) => ({
        day: row.day,
        warningCount: row.warningCount,
        errorCount: row.errorCount,
        capacityHours: row.capacityHours,
      }));
    }
    if (tables.effectiveIds.size === 0) return [];
    const capacityByDay = capacityByDayFromDevices(dailyUptimeByDevice, tables.effectiveIds);
    return fleetRows.map((row) => {
      let warningCount = 0;
      let errorCount = 0;
      for (const issue of issuesByDeviceDay) {
        if (issue.day !== row.day || !tables.effectiveIds.has(issue.deviceId)) continue;
        warningCount += issue.warningCount;
        errorCount += issue.errorCount;
      }
      return {
        day: row.day,
        warningCount,
        errorCount,
        capacityHours: capacityByDay.get(row.day) ?? 0,
      };
    });
  }, [
    dailyUptimeByDevice,
    fleetRows,
    issuesByDeviceDay,
    tables.effectiveIds,
    tables.useFleetSeries,
  ]);

  const tableCount = tables.useFleetSeries ? fleetTableCount : tables.effectiveIds.size;
  const byDay = useMemo(() => new Map(rows.map((row) => [row.day, row])), [rows]);

  const weeks = useMemo(() => {
    if (!fullWeeks || tableCount <= 0) return [];
    return groupFullMondayWeeks(
      rows.map((row) => ({
        day: row.day,
        uptimeHours: 0,
        scheduledHours: 0,
        capacityHours: row.capacityHours,
        unmeasuredHours: 0,
        expectedHours: 0,
      })),
      tableCount,
    );
  }, [fullWeeks, rows, tableCount]);

  const bars: BarChartItem[] = useMemo(() => {
    if (fullWeeks) {
      return weeks.map((week) => {
        const { warningCount, errorCount } = weekTotals(week, byDay);
        const total = warningCount + errorCount;
        return {
          label: formatWeekAxisLabel(week.monday, week.sunday),
          value: total,
          valueLabel: String(total),
          stack:
            total > 0
              ? [
                  { value: warningCount, fill: WARN_FILL, label: "Warnings" },
                  { value: errorCount, fill: ERR_FILL, label: "Errors" },
                ]
              : [],
        };
      });
    }
    return rows.map((row) => {
      const total = row.warningCount + row.errorCount;
      return {
        ...dailyChartBarAxisProps(row.day),
        value: total,
        valueLabel: String(total),
        stack:
          total > 0
            ? [
                { value: row.warningCount, fill: WARN_FILL, label: "Warnings" },
                { value: row.errorCount, fill: ERR_FILL, label: "Errors" },
              ]
            : [],
      };
    });
  }, [byDay, fullWeeks, rows, weeks]);

  const shownWarnings = fullWeeks
    ? weeks.reduce((sum, week) => sum + weekTotals(week, byDay).warningCount, 0)
    : rows.reduce((sum, row) => sum + row.warningCount, 0);
  const shownErrors = fullWeeks
    ? weeks.reduce((sum, week) => sum + weekTotals(week, byDay).errorCount, 0)
    : rows.reduce((sum, row) => sum + row.errorCount, 0);
  const showValueLabels = bars.length > 0 && bars.length <= VALUE_LABEL_MAX_BARS;

  const openIssues = (
    severity: "warning" | "error" | undefined,
    from: string,
    to: string,
    e?: Pick<MouseEvent, "button" | "ctrlKey" | "metaKey">,
  ) => {
    onOpenRawEvents(
      {
        eventName: "client_error",
        ...(severity ? { clientErrorBucket: severity } : {}),
        drillDownFrom: from,
        drillDownTo: to,
      },
      e,
    );
  };

  return (
    <ChartCard
      compact
      title="Errors / Warnings by Day"
      titleRight={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <label className="inline-flex cursor-pointer items-center gap-1.5 text-[12px] font-medium text-text-primary">
            <input
              type="checkbox"
              className="accent-accent-2"
              checked={fullWeeks}
              onChange={(e) => onFullWeeksChange(e.target.checked)}
            />
            Full weeks
          </label>
          <span className="shrink-0 text-[12px] font-medium tabular-nums text-text-secondary">
            ⚠ {shownWarnings.toLocaleString()} · ⛔ {shownErrors.toLocaleString()}
          </span>
        </div>
      }
      criteria={CRITERIA}
      note={
        fullWeeks
          ? "One bar per complete Monday–Sunday in the range. Partial weeks (including this week if it has not finished) are omitted. Yellow = warnings; red = errors."
          : "Same warning/error split as Top Issues. Yellow = warnings; red = errors."
      }
    >
      <div className="mb-1 flex justify-end">
        <ChartTablePicker
          devices={devices}
          selectedIds={tables.effectiveIds}
          onSelectedIdsChange={tables.onSelectedIdsChange}
          locked={tables.filterLocksSingle}
        />
      </div>
      {!tables.useFleetSeries && tables.effectiveIds.size === 0 ? (
        <p className="py-5 text-[13px] text-text-secondary">Select at least one table.</p>
      ) : fullWeeks && bars.length === 0 ? (
        <p className="py-5 text-[13px] text-text-secondary">
          {FULL_WEEKS_EMPTY_MESSAGE}
        </p>
      ) : (
        <BarChart
          data={bars}
          fillBars
          compact={dailyChartRotateAxisLabels(bars.length)}
          verbatimAxisLabels
          rotateAxisLabels={dailyChartRotateAxisLabels(bars.length)}
          axisLabelMaxChars={fullWeeks ? 12 : 10}
          showYAxis
          showValueLabels={showValueLabels}
          hideStackSegmentLabels
          legend={[
            { label: "Warnings", fill: WARN_FILL },
            { label: "Errors", fill: ERR_FILL },
          ]}
          barTooltip={(item, index, segmentIndex) => {
            const severity =
              segmentIndex === 0 ? "warning" : segmentIndex === 1 ? "error" : undefined;
            const severityBit =
              severity === "warning"
                ? "warnings"
                : severity === "error"
                  ? "errors"
                  : "warnings + errors";
            if (fullWeeks) {
              const week = weeks[index];
              if (!week) return item.label;
              const { warningCount, errorCount } = weekTotals(week, byDay);
              return `${formatDashboardDayLabelLong(week.monday)} – ${formatDashboardDayLabelLong(week.sunday)}: ⚠ ${warningCount.toLocaleString()} · ⛔ ${errorCount.toLocaleString()}. Click: Raw Events → client_error (${severityBit}) for this week. Middle-click / Ctrl/Cmd+click: new tab.`;
            }
            const row = rows[index];
            if (!row) return item.label;
            return `${formatDashboardDayLabelLong(row.day)}: ⚠ ${row.warningCount.toLocaleString()} · ⛔ ${row.errorCount.toLocaleString()}. Click: Raw Events → client_error (${severityBit}) for this day. Middle-click / Ctrl/Cmd+click: new tab.`;
          }}
          onBarClick={(_item, index, segmentIndex, e) => {
            const severity =
              segmentIndex === 0 ? "warning" : segmentIndex === 1 ? "error" : undefined;
            if (fullWeeks) {
              const week = weeks[index];
              if (!week) return;
              openIssues(severity, week.monday, week.sunday, e);
              return;
            }
            const row = rows[index];
            if (!row?.day) return;
            openIssues(severity, row.day, row.day, e);
          }}
        />
      )}
    </ChartCard>
  );
}
